import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';

// Async thunks
export const fetchAllRestaurants = createAsyncThunk(
  'restaurants/fetchAllRestaurants',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const lastFetched = state.restaurants.lastFetched;
      const cacheAge = Date.now() - lastFetched;
      
      // Return cached data if it's less than 15 minutes old
      if (lastFetched && cacheAge < 15 * 60 * 1000) {
        return state.restaurants.items;
      }
      
      const response = await axios.get(API_ENDPOINTS.RESTAURANTS_ALL);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch restaurants');
    }
  }
);

export const fetchRestaurantById = createAsyncThunk(
  'restaurants/fetchRestaurantById',
  async (id, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const existingRestaurant = state.restaurants.items.find(r => r._id === id);
      const lastFetched = existingRestaurant?.lastFetched;
      const cacheAge = Date.now() - (lastFetched || 0);
      
      // Return cached data if it's less than 10 minutes old
      if (lastFetched && cacheAge < 10 * 60 * 1000) {
        return existingRestaurant;
      }
      
      const response = await axios.get(API_ENDPOINTS.RESTAURANT_BY_ID(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch restaurant');
    }
  }
);

export const registerRestaurant = createAsyncThunk(
  'restaurants/registerRestaurant',
  async (restaurantData, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_ENDPOINTS.RESTAURANTS_REGISTER, restaurantData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to register restaurant');
    }
  }
);

export const updateRestaurant = createAsyncThunk(
  'restaurants/updateRestaurant',
  async ({ id, updates }, { rejectWithValue }) => {
    try {
      const response = await axios.put(API_ENDPOINTS.RESTAURANT_UPDATE(id), updates);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update restaurant');
    }
  }
);

export const toggleRestaurantStatus = createAsyncThunk(
  'restaurants/toggleRestaurantStatus',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.put(API_ENDPOINTS.RESTAURANT_TOGGLE_STATUS(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to toggle restaurant status');
    }
  }
);

export const deleteRestaurant = createAsyncThunk(
  'restaurants/deleteRestaurant',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(API_ENDPOINTS.RESTAURANT_DELETE(id));
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete restaurant');
    }
  }
);

const initialState = {
  items: [],
  loading: false,
  error: null,
  lastFetched: null,
  currentRestaurant: null,
};

const restaurantSlice = createSlice({
  name: 'restaurants',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentRestaurant: (state, action) => {
      state.currentRestaurant = action.payload;
    },
    clearCurrentRestaurant: (state) => {
      state.currentRestaurant = null;
    },
    invalidateCache: (state) => {
      state.lastFetched = null;
      state.items.forEach(item => {
        if (item.lastFetched) {
          item.lastFetched = null;
        }
      });
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All Restaurants
      .addCase(fetchAllRestaurants.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllRestaurants.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.map(item => ({
          ...item,
          lastFetched: Date.now()
        }));
        state.lastFetched = Date.now();
      })
      .addCase(fetchAllRestaurants.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch Restaurant by ID
      .addCase(fetchRestaurantById.fulfilled, (state, action) => {
        const existingIndex = state.items.findIndex(item => item._id === action.payload._id);
        const restaurantWithTimestamp = {
          ...action.payload,
          lastFetched: Date.now()
        };
        
        if (existingIndex !== -1) {
          state.items[existingIndex] = restaurantWithTimestamp;
        } else {
          state.items.push(restaurantWithTimestamp);
        }
        
        state.currentRestaurant = restaurantWithTimestamp;
      })
      
      // Register Restaurant
      .addCase(registerRestaurant.fulfilled, (state, action) => {
        state.items.push({
          ...action.payload,
          lastFetched: Date.now()
        });
      })
      
      // Update Restaurant
      .addCase(updateRestaurant.fulfilled, (state, action) => {
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = {
            ...action.payload,
            lastFetched: Date.now()
          };
        }
        
        if (state.currentRestaurant?._id === action.payload._id) {
          state.currentRestaurant = {
            ...action.payload,
            lastFetched: Date.now()
          };
        }
      })
      
      // Toggle Restaurant Status
      .addCase(toggleRestaurantStatus.fulfilled, (state, action) => {
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = {
            ...action.payload,
            lastFetched: Date.now()
          };
        }
        
        if (state.currentRestaurant?._id === action.payload._id) {
          state.currentRestaurant = {
            ...action.payload,
            lastFetched: Date.now()
          };
        }
      })
      
      // Delete Restaurant
      .addCase(deleteRestaurant.fulfilled, (state, action) => {
        state.items = state.items.filter(item => item._id !== action.payload);
        if (state.currentRestaurant?._id === action.payload) {
          state.currentRestaurant = null;
        }
      });
  },
});

// Selectors
export const selectAllRestaurants = (state) => state.restaurants.items;
export const selectRestaurantLoading = (state) => state.restaurants.loading;
export const selectRestaurantError = (state) => state.restaurants.error;
export const selectCurrentRestaurant = (state) => state.restaurants.currentRestaurant;
export const selectRestaurantById = (state, id) => 
  state.restaurants.items.find(item => item._id === id);

export const selectActiveRestaurants = (state) => 
  state.restaurants.items.filter(item => item.isActive !== false);

export const selectRestaurantsByCategory = (state, category) => 
  state.restaurants.items.filter(item => item.category === category);

export const { 
  clearError, 
  setCurrentRestaurant, 
  clearCurrentRestaurant, 
  invalidateCache 
} = restaurantSlice.actions;

export default restaurantSlice.reducer;

